import React from "react";
import { PlanningEvent } from "../utils";
import { cn } from "../../../../utils";
import { isBefore } from "date-fns";
import { Color } from "@ac-expo/api-types";
import { formatRGBColor } from "@ac-expo/utils";

type SingleEventCardProps = {
  withRegistration?: boolean;
  event: PlanningEvent;
  hours: string;
  isSlim?: boolean;
  date?: string;
  openDetails?: () => void;
  allDayBlockColor?: Color;
  isMobile?: boolean;
  isCompact?: boolean;
};

const SingleEventCard = ({
  withRegistration = false,
  isSlim = true,
  event,
  hours,
  date,
  openDetails,
  allDayBlockColor = "orange",
  isMobile = false,
  isCompact = false,
}: SingleEventCardProps) => {
  const { title, isRegistered, allDay, end, blockColor, hasAvailableTicketTypes = true } = event;
  const now = new Date();
  const isDisabled = isBefore(end, now);
  const localWithRegistration =
    (withRegistration ||
      (!isDisabled && event.isAvailableOnDisabledTicketing === true)) &&
    hasAvailableTicketTypes;

  const onSignUpClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    openDetails?.();
  };

  const cardProps = {
    isRegistered: !!isRegistered,
    activityName: title,
    openDetails: openDetails,
    onSignUpClick: onSignUpClick,
    withRegistration: localWithRegistration,
    isDisabled,
    blockColor: blockColor ?? "blue",
    allDayBlockColor,
    date,
    isMobile,
    isCompact,
  };

  if (isSlim) {
    return <SlimCard {...cardProps} hours={hours} />;
  }

  if (allDay) {
    return <SlimCard {...cardProps} title={title} isAllDay />;
  }

  return <StandardCard {...cardProps} hours={hours} title={title} />;
};

type RegistrationBadgeProps = {
  isRegistered: boolean;
  onSignUpClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  isMobile?: boolean;
  isDisabled?: boolean;
};

const RegistrationBadge = ({ isRegistered, onSignUpClick, isMobile, isDisabled }: RegistrationBadgeProps) => {
  if (isRegistered) {
    return (
      <div
        className={cn(
          "rounded border border-gray-300 bg-gray-100 px-4 text-gray-400",
          { "px-2 py-1 text-xs": isMobile }
        )}
      >
        Inscrit
      </div>
    );
  }

  return (
    <button
      onClick={onSignUpClick}
      className={cn(
        "rounded border border-blue-400 bg-white px-4 text-blue-400",
        { "px-2 py-1 text-xs": isMobile },
        { "text-gray-400": isDisabled, "border-gray-400": isDisabled }
      )}
      disabled={isDisabled}
    >
      S&apos;inscrire
    </button>
  );
};

type SlimCardHoursDisplayProps = {
  isAllDay: boolean;
  hours?: string;
  date?: string;
};

const SlimCardHoursDisplay = ({ isAllDay, hours, date }: SlimCardHoursDisplayProps) => {
  if (isAllDay) {
    return <p className="text-xs font-semibold text-white">Jour entier</p>;
  }

  if (hours) {
    return (
      <p className="text-xs font-semibold text-white text-start">
        {date ? `${date} • ` : ""}
        {hours}
      </p>
    );
  }

  if (date) {
    return <p className="text-xs font-semibold text-white">{date}</p>;
  }

  return null;
};

type CardProps = {
  activityName: string;
  hours?: string;
  title?: string;
  isRegistered: boolean;
  openDetails?: () => void;
  onSignUpClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  withRegistration: boolean;
  isDisabled?: boolean;
  blockColor: Color;
  allDayBlockColor: Color;
  date?: string;
  isAllDay?: boolean;
  isMobile?: boolean;
  isCompact?: boolean;
};
const SlimCard = ({
  isRegistered,
  activityName,
  hours,
  title,
  openDetails,
  onSignUpClick,
  withRegistration,
  isDisabled,
  blockColor,
  allDayBlockColor,
  date,
  isAllDay,
  isMobile,
  isCompact,
}: CardProps) => {
  const bgColor = isAllDay ? allDayBlockColor : blockColor;
  const useCompactPadding = isCompact || (isAllDay && isMobile);

  return (
    <div
      role="button"
      tabIndex={0}
      className={cn(
        "flex size-full flex-col justify-center overflow-hidden rounded-lg",
        useCompactPadding ? "p-2" : "p-4",
        { "cursor-default": !openDetails && !isDisabled }
      )}
      style={{
        backgroundColor: formatRGBColor(bgColor),
      }}
      title={activityName}
      onClick={openDetails && !isDisabled ? openDetails : undefined}
      onKeyDown={openDetails && !isDisabled ? openDetails : undefined}
    >
      <div className="flex flex-wrap items-center justify-between">
        <div className="flex w-full flex-col">
          <SlimCardHoursDisplay isAllDay={!!isAllDay} hours={hours} date={date} />

          {title ? (
            <p className="truncate text-sm font-semibold text-white text-start">
              {title}
            </p>
          ) : null}
        </div>
        {withRegistration && !isDisabled ? (
          <RegistrationBadge
            isRegistered={isRegistered}
            onSignUpClick={onSignUpClick}
            isMobile={isMobile}
          />
        ) : null}
      </div>
    </div>
  );
};

const StandardCard = ({
  activityName,
  hours,
  title,
  isRegistered,
  openDetails,
  onSignUpClick,
  withRegistration,
  isDisabled,
  blockColor,
  date,
}: CardProps & { title: string; hours: string }) => {
  return (
    <div
      role="button"
      tabIndex={0}
      className={cn("size-full overflow-hidden rounded-lg p-2", {
        "cursor-default": !openDetails || isDisabled,
      })}
      style={{
        backgroundColor: formatRGBColor(blockColor),
      }}
      title={activityName}
      onClick={openDetails && !isDisabled ? openDetails : undefined}
      onKeyDown={openDetails && !isDisabled ? openDetails : undefined}
    >
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-white">
          {date ? `${date} • ` : ""}
          {hours}
        </p>
        {withRegistration ? (
          <RegistrationBadge
            isRegistered={isRegistered}
            onSignUpClick={onSignUpClick}
            isDisabled={isDisabled}
          />
        ) : null}
      </div>
      <p className="truncate text-sm font-semibold text-white">{title}</p>
    </div>
  );
};
export default SingleEventCard;
